import { TableProps as TablePropsAntd, TablePaginationConfig } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import { useSearchParamsContext } from 'contexts/SearchParamsContext';

import { LIMIT_OPTIONS } from '../../constants';
import * as Styled from './Table.styles';

type TableProps<TData> = {
  data: TData[];
  numberOfRecords?: number;
  isLoading?: boolean;
  pagination?: boolean;
  config: ColumnsType<TData>;
  rowKey?: (record: TData) => string | number;
  rowClassName?: (record: TData) => string;
  onRowClick?: (record: TData) => void;
  rowSelection?: TableRowSelection<TData>;
};

const Table = <TData extends object>({
  config,
  data,
  numberOfRecords = 0,
  isLoading,
  pagination,
  rowClassName,
  rowKey,
  onRowClick,
  rowSelection,
}: TableProps<TData>) => {
  const { searchParams, updateSearchParams } = useSearchParamsContext();
  const currentPage = parseInt(String(searchParams.page), 10) || 1;
  const currentLimit = parseInt(String(searchParams.limit), 10) || 1;

  const tablePagination: TablePropsAntd<TData>['pagination'] = pagination
    ? {
        current: currentPage,
        pageSize: currentLimit,
        position: ['bottomRight'],
        total: numberOfRecords,
        pageSizeOptions: LIMIT_OPTIONS,
        showTotal: (total: number, range: [number, number]) =>
          `${range[0]}–${range[1]} of ${total}`,
        showSizeChanger: true,
        onShowSizeChange: () => {},
        onChange: () => {},
      }
    : false;

  return (
    <Styled.Table
      rowSelection={rowSelection}
      columns={config}
      dataSource={data}
      loading={isLoading}
      pagination={tablePagination}
      rowClassName={rowClassName}
      rowKey={rowKey}
      onRow={(record) => (onRowClick ? { onClick: () => onRowClick(record) } : {})}
      onChange={(pagination: TablePaginationConfig) => {
        updateSearchParams({
          limit: pagination.pageSize,
          page: pagination.pageSize !== currentLimit ? 1 : pagination.current,
        });
      }}
    />
  );
};

export default Table;
